import {useState} from 'react';
import {I18n} from 'aws-amplify/utils';
import {
    Alert,
    Button,
    Group,
    Select,
    Stack,
    Text,
} from '@mantine/core';

import {useDnsRecordsFilter} from '../hooks/useDnsRecordsQuery';
import {DELEGATION_RECORD_TYPE} from '../types';

// FNP-1192 — the type filter.
//
// ⚠️ ONE FIELD, AND IT IS SERVER-SIDE. `listDnsRecords` takes an exact `type`,
// so applying this re-queries from page one against a different result set
// rather than narrowing the loaded rows. That is why the draft state below is
// local and only Apply writes it to the module-scoped store: re-querying on
// every keystroke of a dropdown would be a request per selection.
//
// ⚠️ NS IS NOT JUST ANOTHER OPTION. The nameserver records for a delegated
// subdomain ARE the delegation, so filtering to them is what makes the
// delegation chain visible in the interface. It gets its own labelled shortcut
// rather than sitting anonymously in an alphabetical list.
export const FilterForm = (
    {
        recordTypes,
        onApply,
        onCancel,
    }: {
        recordTypes: string[],
        onApply: () => void,
        onCancel: () => void,
    },
) => {

    const {
        filters,
        handleSetFilters,
        handleClearFilters,
        handleShowDelegation,
    } = useDnsRecordsFilter();

    // Seeded from the store so reopening the drawer shows what is applied,
    // rather than an empty form next to a filtered table.
    const [type, setType] = useState<string>(filters.type ?? '');

    const apply = (nextType: string) => {
        // An empty value is DROPPED rather than stored. A stored empty string
        // would count toward the active-filter badge and read as a filter that
        // is on while matching everything.
        if (nextType) {
            handleSetFilters({type: nextType});
        } else {
            handleClearFilters();
        }
        onApply();
    };

    return (
        <Stack gap={'lg'}>

            {/* THE DELEGATION SHORTCUT, above the generic picker because it is
                the filter with a reason rather than one value among many. */}
            <Alert variant={'light'} color={'grape'} title={I18n.get('See the delegation')}>
                <Stack gap={'xs'}>
                    <Text size={'sm'}>
                        {I18n.get('Nameserver records are what delegate a subdomain to another provider. Filtering to them shows the delegation chain this zone publishes.')}
                    </Text>
                    <Group>
                        <Button
                            size={'xs'}
                            variant={'light'}
                            color={'grape'}
                            onClick={() => {
                                setType(DELEGATION_RECORD_TYPE);
                                handleShowDelegation();
                                onApply();
                            }}
                        >
                            {I18n.get('Show nameserver records')}
                        </Button>
                    </Group>
                </Stack>
            </Alert>

            <Select
                label={I18n.get('Record type')}
                description={I18n.get('Filtering by type asks the provider for that type only, so it narrows the whole zone rather than the rows already loaded.')}
                placeholder={I18n.get('Any type')}
                data={recordTypes}
                value={type === '' ? null : type}
                onChange={next => setType(next ?? '')}
                clearable={true}
                searchable={true}
            />

            <Group justify={'space-between'} mt={'xl'}>
                <Button variant={'outline'} onClick={onCancel}>
                    {I18n.get('Cancel')}
                </Button>
                <Group gap={'xs'}>
                    <Button
                        variant={'outline'}
                        onClick={() => {
                            setType('');
                            handleClearFilters();
                        }}
                    >
                        {I18n.get('Clear')}
                    </Button>
                    <Button onClick={() => apply(type)}>
                        {I18n.get('Apply')}
                    </Button>
                </Group>
            </Group>

        </Stack>
    );
};
